import React, { useState } from 'react';
import { Input, Button } from '@ftdr/blueprint-components-react';
import REText from '@components/wrappedBDS/REText';
import { getCurrentThemeConfig } from '@app/core/theme-config';
import { API as ProductApi } from '@apis/product.api';
import { ProductAvailabilityRequest, ProductAndCoveragesResponse } from '@apis/models';

interface PromoCodeInputProps {
  disabled?: boolean;
  productAvailabilityRequest?: ProductAvailabilityRequest;
  availablePlansAndCoverages?: ProductAndCoveragesResponse[];
  listingDays?: number;
  earnixRuleID?: number;
  initiatingOfficeId?: string;
  initiatingOfficeFranchiseCode?: string;
  autoAppliedCode?: string;
  isBuyerPay?: boolean;
  value?: string;
  isValid?: boolean;
  onValidationSuccess?: (promoCode: string) => void;
  onValidationError?: (error?: string) => void;
}

const PromoCodeInput: React.FC<PromoCodeInputProps> = ({
  disabled = false,
  productAvailabilityRequest,
  availablePlansAndCoverages = [],
  listingDays,
  earnixRuleID,
  initiatingOfficeId,
  initiatingOfficeFranchiseCode,
  isBuyerPay,
  value = '',
  autoAppliedCode = '',
  isValid = false,
  onValidationSuccess,
  onValidationError,
}) => {
  const themeConfig = getCurrentThemeConfig();
  const productApi = new ProductApi();

  const [promoCode, setPromoCode] = useState<string>(value);
  const [error, setError] = useState<string>(null);
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [lastValidatedCode, setLastValidatedCode] = useState<string>('');
  const [hasBeenCleared, setHasBeenCleared] = useState<boolean>(false);

  React.useEffect(() => {
    if (!hasBeenCleared && value !== promoCode) {
      setPromoCode(value);
    }
    if (isValid && value) {
      setLastValidatedCode(value);
      setHasBeenCleared(false);
    }
  }, [value, isValid]);

  const success =
    isValid && lastValidatedCode
      ? `Promotion code ${lastValidatedCode} applied`
      : autoAppliedCode
        ? `Promotion code ${autoAppliedCode} applied`
        : null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setPromoCode(newValue);
    setError(null);
    setHasBeenCleared(false);
    if (newValue !== lastValidatedCode && newValue.trim() === '') {
      onValidationError?.();
    }
  };

  const displayPlaceholder = lastValidatedCode || autoAppliedCode || '';

  const validatePromoCodeRequest = async (code: string) => {
    try {
      const starPVIDs =
        availablePlansAndCoverages?.map((p) => p?.product?.starPVID).filter(Boolean) || [];

      if (!productAvailabilityRequest?.property) {
        throw new Error('Property information is required for promo validation');
      }

      const response = await productApi.validatePromos({
        property: { ...productAvailabilityRequest.property },
        options: {
          ...productAvailabilityRequest.options,
          listingTermInDays: listingDays,
          earnixRuleID: earnixRuleID,
          initiatingOfficeID: initiatingOfficeId,
          initiatingOfficeFranchiseCode: initiatingOfficeFranchiseCode,
          specialDiscounts: [],
          buyerPaid: isBuyerPay,
          voucherCodes: [code],
        },
        starPVIDs,
      });

      if (response.valid) {
        setError(null);
        setLastValidatedCode(code);
        setPromoCode('');
        setHasBeenCleared(true);
        onValidationSuccess?.(code);
      } else {
        const errorMsg = `Promotion code ${code} is not valid`;
        setError(errorMsg);
        setLastValidatedCode('');
        setPromoCode('');
        setHasBeenCleared(true);
        onValidationError?.(errorMsg);
      }
    } catch (err) {
      const errorMsg =
        err?.response?.data?.message || err?.message || `Promotion code ${code} is not valid`;
      setError(errorMsg);
      setLastValidatedCode('');
      setPromoCode('');
      setHasBeenCleared(true);
      onValidationError?.(errorMsg);
    } finally {
      setIsValidating(false);
    }
  };

  const handleValidateClick = async () => {
    if (!promoCode.trim()) {
      const errorMsg = 'Please enter a promo code';
      setError(errorMsg);
      setLastValidatedCode('');
      onValidationError?.(errorMsg);
      return;
    }

    setIsValidating(true);
    setError(null);

    await validatePromoCodeRequest(promoCode.trim());
  };

  return (
    <div className="promocode-input-container">
      <REText className="mb-2 text-label">Promotion Code</REText>
      <div className="flex gap-2">
        <Input
          id="promo-code-input"
          label=""
          value={promoCode}
          onChange={handleInputChange}
          disabled={disabled || isValidating}
          error={error}
          formField
          className="flex-1 min-w-0"
          success={success}
          placeholder={displayPlaceholder}
        />
        <Button
          id="validate-promo-btn"
          label="Validate"
          color={themeConfig.promoCodeValidateButtonColor}
          variant="filled"
          onClick={handleValidateClick}
          disabled={disabled || isValidating}
          shape={themeConfig.buttonThemeShape}
          className="flex-shrink-0"
        />
      </div>
    </div>
  );
};

export default PromoCodeInput;
